import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle, AlertCircle, Activity, Settings, Plus, Brain, Zap, Clock, TrendingUp } from 'lucide-react';
import { apiClient } from '../../services/api';
import { relativeTime } from '../../utils/relativeTime';

// KQA-10: anchor demo "last synced" times to "now" so connected integrations
// show a live relative timestamp ("12 minutes ago") instead of a stale 2024 date.
function minutesAgoISO(minutes: number): string {
  return new Date(Date.now() - minutes * 60_000).toISOString();
}

interface LiveIntegration {
  id: number;
  type: string;
  status?: string;
  is_active: boolean;
  last_sync_at?: string;
}

// Integration ids that the real config flow (/integrations) supports.
const CONFIGURABLE_TYPES: Record<string, string> = {
  slack: 'slack',
  teams: 'teams',
  jira: 'jira',
  github: 'github',
};

export function Integrations() {
  const [activeCategory, setActiveCategory] = useState('all');
  const navigate = useNavigate();
  // KQA-10: live integration records keyed by type, so "Last synced" reflects
  // real last_sync_at as a relative timestamp instead of a stale 2024 string.
  const [liveByType, setLiveByType] = useState<Record<string, LiveIntegration>>({});

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<LiveIntegration[]>('/api/v1/integrations/')
      .then(({ data }) => {
        if (cancelled) return;
        const map: Record<string, LiveIntegration> = {};
        for (const i of data) {
          // Keep the most recently synced record per type.
          const existing = map[i.type];
          if (
            !existing ||
            (i.last_sync_at && (!existing.last_sync_at || i.last_sync_at > existing.last_sync_at))
          ) {
            map[i.type] = i;
          }
        }
        setLiveByType(map);
      })
      .catch(() => {
        // App stays fully usable with zero integrations connected.
        setLiveByType({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Resolve display status + last-synced from the live record when present.
  const resolveStatus = (staticStatus: string, type: string): string => {
    const live = liveByType[type];
    if (!live) return staticStatus === 'connected' ? 'available' : staticStatus;
    return live.is_active ? (live.status || 'connected') : 'available';
  };

  // Prefer the live record's last_sync_at; otherwise fall back to the seeded
  // (relative-to-now) demo timestamp. Returns null when nothing is available.
  const resolveLastSync = (type: string, staticLastSync: string | null): string | null => {
    const live = liveByType[type];
    const source = live?.last_sync_at ?? staticLastSync;
    if (!source) return null;
    return relativeTime(source, 'Not yet synced');
  };

  // Route to the real config flow at /integrations. Configurable types
  // deep-link to their tab; others land on the integrations page with a note.
  const handleConfigure = (integrationId: string, name: string) => {
    const tab = CONFIGURABLE_TYPES[integrationId];
    if (tab) {
      navigate(`/integrations?tab=${tab}`);
    } else {
      toast.info(`${name} uses the generic webhook flow — configure it from Integrations.`);
      navigate('/integrations');
    }
  };

  // Integration categories
  const categories = [
    { id: 'all', name: 'All Integrations', count: 13 },
    { id: 'communication', name: 'Communication', count: 4 },
    { id: 'project-management', name: 'Project Management', count: 4 },
    { id: 'capacity-planning', name: 'Capacity Planning', count: 5 }
  ];

  // Available integrations
  const integrations = [
    {
      id: 'teams',
      name: 'Microsoft Teams',
      category: 'communication',
      description: 'AI-powered meeting summarization and action item extraction',
      logo: '🟦',
      status: 'connected',
      lastSync: minutesAgoISO(12),
      features: [
        'Automated meeting transcription',
        'AI-powered summary generation',
        'Action item extraction with assignees',
        'Auto-sync to project action items',
        'Meeting insights and analytics'
      ],
      stats: {
        meetingsProcessed: 47,
        actionItemsExtracted: 124,
        summariesGenerated: 47,
        avgProcessingTime: '2 min'
      },
      config: {
        autoSync: true,
        projectMapping: 'automatic',
        actionItemStatus: 'pending',
        notifyAssignees: true
      }
    },
    {
      id: 'outlook',
      name: 'Microsoft Outlook',
      category: 'communication',
      description: 'Email monitoring and action item extraction from project emails',
      logo: '📧',
      status: 'connected',
      lastSync: minutesAgoISO(47),
      features: [
        'Project email monitoring (by ID in subject)',
        'AI-powered action item extraction',
        'Automatic assignee detection',
        'Due date identification',
        'Email-to-project linking'
      ],
      stats: {
        emailsTracked: 156,
        actionItemsExtracted: 87,
        projectsMonitored: 12,
        avgConfidence: '94%'
      },
      config: {
        autoProcess: true,
        extractActions: true,
        notifyAssignees: true,
        confidenceThreshold: '90%'
      }
    },
    {
      id: 'jira',
      name: 'Jira',
      category: 'project-management',
      description: 'Sync work breakdown structure, issues, and project data',
      logo: '🔷',
      status: 'connected',
      lastSync: minutesAgoISO(135),
      features: [
        'Bi-directional WBS sync',
        'Issue tracking integration',
        'Sprint and epic mapping',
        'Status synchronization',
        'Time tracking integration'
      ],
      stats: {
        projectsSynced: 8,
        issuesSynced: 342,
        lastUpdate: '15 min ago',
        syncFrequency: 'Every 30 min'
      },
      config: {
        syncDirection: 'bidirectional',
        autoCreateTasks: true,
        statusMapping: true,
        priorityMapping: true
      }
    },
    {
      id: 'monday',
      name: 'Monday.com',
      category: 'capacity-planning',
      description: 'Resource capacity planning and workload management',
      logo: '🟧',
      status: 'available',
      lastSync: null,
      features: [
        'Resource capacity tracking',
        'Workload balancing',
        'Timeline synchronization',
        'Team availability management',
        'Automated resource allocation'
      ],
      stats: null,
      config: null
    },
    {
      id: 'forecast',
      name: 'Forecast',
      category: 'capacity-planning',
      description: 'AI-powered resource and capacity planning',
      logo: '📊',
      status: 'available',
      lastSync: null,
      features: [
        'Predictive resource planning',
        'Capacity forecasting',
        'Project scheduling',
        'Budget tracking',
        'Team utilization analytics'
      ],
      stats: null,
      config: null
    },
    {
      id: 'wrike',
      name: 'Wrike',
      category: 'capacity-planning',
      description: 'Work management and resource planning integration',
      logo: '🟩',
      status: 'available',
      lastSync: null,
      features: [
        'Resource workload management',
        'Project timeline sync',
        'Task dependencies',
        'Time tracking',
        'Custom field mapping'
      ],
      stats: null,
      config: null
    },
    {
      id: 'smartsheet',
      name: 'Smartsheet',
      category: 'capacity-planning',
      description: 'Spreadsheet-based capacity and resource planning',
      logo: '📋',
      status: 'available',
      lastSync: null,
      features: [
        'Grid-based resource planning',
        'Gantt chart synchronization',
        'Resource allocation tracking',
        'Sheet-to-project mapping',
        'Automated updates'
      ],
      stats: null,
      config: null
    },
    {
      id: 'resource-guru',
      name: 'Resource Guru',
      category: 'capacity-planning',
      description: 'Simple resource scheduling and capacity planning',
      logo: '👥',
      status: 'available',
      lastSync: null,
      features: [
        'Resource scheduling',
        'Availability tracking',
        'Leave management',
        'Utilization reports',
        'Clash detection'
      ],
      stats: null,
      config: null
    },
    {
      id: 'slack',
      name: 'Slack',
      category: 'communication',
      description: 'Team communication and notification integration',
      logo: '💬',
      status: 'available',
      lastSync: null,
      features: [
        'Project notifications',
        'Status updates',
        'Action item reminders',
        'Approval workflows',
        'Channel mapping'
      ],
      stats: null,
      config: null
    },
    {
      id: 'zoom',
      name: 'Zoom',
      category: 'communication',
      description: 'Video meeting transcription and action item extraction',
      logo: '🎥',
      status: 'available',
      lastSync: null,
      features: [
        'Meeting transcription',
        'Recording analysis',
        'Action item extraction',
        'Participant tracking',
        'Meeting insights'
      ],
      stats: null,
      config: null
    },
    {
      id: 'azure-devops',
      name: 'Azure DevOps',
      category: 'project-management',
      description: 'DevOps pipeline and work item integration',
      logo: '🔵',
      status: 'available',
      lastSync: null,
      features: [
        'Work item synchronization',
        'Pipeline status tracking',
        'Board integration',
        'Sprint planning sync',
        'Repository linking'
      ],
      stats: null,
      config: null
    },
    {
      id: 'asana',
      name: 'Asana',
      category: 'project-management',
      description: 'Task and project management integration',
      logo: '🎯',
      status: 'available',
      lastSync: null,
      features: [
        'Task synchronization',
        'Project timeline sync',
        'Portfolio integration',
        'Custom field mapping',
        'Milestone tracking'
      ],
      stats: null,
      config: null
    },
    {
      id: 'clickup',
      name: 'ClickUp',
      category: 'project-management',
      description: 'All-in-one productivity and project management',
      logo: '✅',
      status: 'available',
      lastSync: null,
      features: [
        'Task and subtask sync',
        'Time tracking integration',
        'Goal alignment',
        'Custom views',
        'Automation rules'
      ],
      stats: null,
      config: null
    }
  ];

  const filteredIntegrations = activeCategory === 'all' 
    ? integrations 
    : integrations.filter(i => i.category === activeCategory);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl mb-2">Integrations Hub</h1>
              <p className="text-indigo-100">
                Connect Karyva with your favorite tools for seamless workflow automation
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg px-4 py-2">
                <div className="text-2xl">{integrations.filter(i => i.status === 'connected').length}</div>
                <div className="text-xs text-indigo-100">Active</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg px-4 py-2">
                <div className="text-2xl">{integrations.length}</div>
                <div className="text-xs text-indigo-100">Available</div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Integration Insights */}
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg">AI Integration Intelligence</h2>
              <p className="text-sm text-gray-600">Smart recommendations for optimizing your integrations</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-700">High Value Integration</span>
              </div>
              <p className="text-sm text-gray-700 mb-2">
                Teams integration extracted 124 action items from 47 meetings - 94% auto-assigned to correct projects
              </p>
              <button className="text-sm text-blue-600 hover:text-blue-700">View Analytics →</button>
            </div>
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-700">High Value</span>
              </div>
              <p className="text-sm text-gray-700 mb-2">
                Outlook integration auto-extracted 87 action items from 156 project emails - 94% accuracy
              </p>
              <button className="text-sm text-blue-600 hover:text-blue-700">View Analytics →</button>
            </div>
            <div className="bg-white rounded-lg p-4 border border-orange-200">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-orange-600" />
                <span className="text-sm text-orange-700">Sync Issue</span>
              </div>
              <p className="text-sm text-gray-700 mb-2">
                Jira sync delayed by 45 min - API rate limit reached. Consider upgrading plan.
              </p>
              <button className="text-sm text-blue-600 hover:text-blue-700">Review Settings →</button>
            </div>
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex items-center gap-3">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                activeCategory === cat.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {cat.name} ({cat.count})
            </button>
          ))}
        </div>

        {/* Integrations Grid */}
        <div className="grid grid-cols-2 gap-6">
          {filteredIntegrations.map((integration) => (
            <IntegrationCard
              key={integration.id}
              integration={{
                ...integration,
                status: resolveStatus(integration.status, integration.id),
                lastSync: resolveLastSync(integration.id, integration.lastSync),
              }}
              onConfigure={handleConfigure}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function IntegrationCard({ integration, onConfigure }: { integration: any; onConfigure: (id: string, name: string) => void }) {
  const [showDetails, setShowDetails] = useState(false);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'connected':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'configuring':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'error':
        return 'bg-red-100 text-red-700 border-red-300';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-all">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="text-4xl">{integration.logo}</div>
            <div>
              <h3 className="text-lg font-medium">{integration.name}</h3>
              <p className="text-sm text-gray-600">{integration.description}</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-md text-xs border flex items-center gap-1 ${getStatusBadge(integration.status)}`}>
            {integration.status === 'connected' && <CheckCircle className="w-3 h-3" />}
            {integration.status === 'error' && <AlertCircle className="w-3 h-3" />}
            {integration.status === 'connected' ? 'Connected' : integration.status === 'available' ? 'Available' : integration.status.charAt(0).toUpperCase() + integration.status.slice(1)}
          </span>
        </div>

        {integration.status === 'connected' && integration.lastSync && (
          <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 rounded px-3 py-2">
            <Clock className="w-3 h-3" />
            Last synced: {integration.lastSync}
          </div>
        )}
      </div>

      {/* Stats (if connected) */}
      {integration.status === 'connected' && integration.stats && (
        <div className="p-6 bg-blue-50 border-b border-gray-200">
          <h4 className="text-sm font-medium mb-3">Integration Statistics</h4>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(integration.stats).map(([key, value]) => (
              <div key={key} className="bg-white rounded p-2">
                <div className="text-xs text-gray-600 mb-1">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </div>
                <div className="font-medium">{String(value)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Features */}
      <div className="p-6 border-b border-gray-200">
        <h4 className="text-sm font-medium mb-3">Key Features</h4>
        <ul className="space-y-2">
          {integration.features.map((feature: string, idx: number) => (
            <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
              <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Actions */}
      <div className="p-6">
        <div className="flex items-center gap-3">
          {integration.status === 'connected' ? (
            <>
              <button
                onClick={() => onConfigure(integration.id, integration.name)}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Configure
              </button>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
              >
                <Activity className="w-4 h-4" />
                {showDetails ? 'Hide Details' : 'View Details'}
              </button>
            </>
          ) : (
            <button
              onClick={() => onConfigure(integration.id, integration.name)}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Connect {integration.name}
            </button>
          )}
        </div>
      </div>

      {/* Configuration Panel (if connected and details shown) */}
      {showDetails && integration.status === 'connected' && integration.config && (
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <h4 className="text-sm font-medium mb-4">Configuration Settings</h4>
          <div className="space-y-3">
            {Object.entries(integration.config).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0">
                <span className="text-sm text-gray-700">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </span>
                <span className="text-sm font-medium">
                  {typeof value === 'boolean' ? (value ? 'Enabled' : 'Disabled') : String(value)}
                </span>
              </div>
            ))}
          </div>
          <button
            onClick={() => onConfigure(integration.id, integration.name)}
            className="w-full mt-4 px-4 py-2 border border-gray-300 rounded-lg hover:bg-white text-sm"
          >
            Edit Configuration
          </button>
        </div>
      )}
    </div>
  );
}

